import { RecordingStateMachine, type RecordingState } from "@mrbd/contracts";

export const MAX_RECORDING_MS = 60_000;
export const MAX_AUDIO_BYTES = 7 * 1024 * 1024;
export const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4",
] as const;

export function selectRecorderMime(MediaRecorderClass: typeof MediaRecorder = MediaRecorder): string | undefined {
  return MIME_CANDIDATES.find((mime) => MediaRecorderClass.isTypeSupported(mime));
}

export interface RecorderDiagnostics {
  apiPresent: boolean;
  mimeType: string;
  dataAvailable: boolean;
  audioBytes: number;
  stopEvent: boolean;
  startedAt?: string;
  stopReason?: string;
  errorName?: string;
  errorMessage?: string;
}

export interface RecordedAudio {
  blob: Blob;
  mimeType: string;
  durationMs: number;
  diagnostics: RecorderDiagnostics;
}

export interface RecorderCallbacks {
  onState: (state: RecordingState) => void;
  onDiagnostics: (diagnostics: RecorderDiagnostics) => void;
  onTick: (elapsedMs: number) => void;
  onAudio: (audio: RecordedAudio) => void | Promise<void>;
  onError: (error: Error) => void;
}

export class RecorderController {
  readonly machine = new RecordingStateMachine();
  readonly callbacks: RecorderCallbacks;
  #recorder: MediaRecorder | null = null;
  #stream: MediaStream | null = null;
  #chunks: Blob[] = [];
  #bytes = 0;
  #startedAt = 0;
  #maxTimer: number | undefined;
  #tickTimer: number | undefined;
  #discard = false;
  #stopReason = "user";
  #tooLarge = false;
  #diagnostics: RecorderDiagnostics = {
    apiPresent: typeof globalThis.navigator?.mediaDevices?.getUserMedia === "function" && typeof globalThis.MediaRecorder === "function",
    mimeType: "not selected",
    dataAvailable: false,
    audioBytes: 0,
    stopEvent: false,
  };

  constructor(callbacks: RecorderCallbacks) {
    this.callbacks = callbacks;
  }

  get state(): RecordingState { return this.machine.state; }

  async start(): Promise<void> {
    if (!["idle", "completed", "error", "cancelled"].includes(this.state)) return;
    this.#transition("requesting-permission");
    this.#resetCapture();
    try {
      if (!navigator.mediaDevices?.getUserMedia || !globalThis.MediaRecorder) {
        throw new Error("Microphone recording API is unavailable in this runtime.");
      }
      this.#stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (this.state !== "requesting-permission") {
        this.#stopTracks();
        return;
      }
      const selectedMime = selectRecorderMime();
      this.#recorder = selectedMime
        ? new MediaRecorder(this.#stream, { mimeType: selectedMime })
        : new MediaRecorder(this.#stream);
      this.#diagnostics.mimeType = this.#recorder.mimeType || selectedMime || "browser default";
      this.#diagnostics.startedAt = new Date().toISOString();
      this.#bindRecorder(this.#recorder);
      this.#recorder.start(250);
      this.#startedAt = performance.now();
      this.#transition("recording");
      this.#maxTimer = window.setTimeout(() => this.stop(false, "maximum-duration"), MAX_RECORDING_MS);
      this.#tickTimer = window.setInterval(() => this.callbacks.onTick(performance.now() - this.#startedAt), 250);
      this.#emitDiagnostics();
    } catch (error) {
      this.#stopTracks();
      this.#fail(error);
    }
  }

  stop(discard = false, reason = "user"): void {
    this.#discard = discard;
    this.#stopReason = reason;
    this.#diagnostics.stopReason = reason;
    if (this.state === "requesting-permission") {
      this.#transition("cancelled");
      this.#stopTracks();
      return;
    }
    if (this.state !== "recording" || !this.#recorder) return;
    this.#transition("stopping");
    this.#clearTimers();
    try {
      if (this.#recorder.state !== "inactive") this.#recorder.stop();
    } catch (error) {
      this.#stopTracks();
      this.#fail(error);
    }
  }

  cancel(reason = "cancelled"): void {
    if (this.state === "recording" || this.state === "requesting-permission") {
      this.stop(true, reason);
    }
  }

  transition(next: RecordingState): void { this.#transition(next); }

  reset(): void {
    if (this.state !== "idle" && this.machine.canTransition("idle")) this.#transition("idle");
  }

  #bindRecorder(recorder: MediaRecorder): void {
    recorder.addEventListener("dataavailable", (event) => {
      this.#diagnostics.dataAvailable = true;
      if (event.data.size > 0) {
        this.#chunks.push(event.data);
        this.#bytes += event.data.size;
        this.#diagnostics.audioBytes = this.#bytes;
      }
      this.#emitDiagnostics();
      if (this.#bytes > MAX_AUDIO_BYTES && this.state === "recording") {
        this.#tooLarge = true;
        this.stop(false, "maximum-size");
      }
    });
    recorder.addEventListener("stop", () => void this.#handleStop());
    recorder.addEventListener("error", () => {
      this.#stopTracks();
      this.#fail(new Error("MediaRecorder reported a recording error."));
    });
  }

  async #handleStop(): Promise<void> {
    this.#diagnostics.stopEvent = true;
    this.#diagnostics.audioBytes = this.#bytes;
    this.#clearTimers();
    this.#stopTracks();
    this.#emitDiagnostics();
    if (this.#discard) {
      if (this.state === "stopping") this.#transition("cancelled");
      return;
    }
    if (this.#tooLarge || this.#bytes > MAX_AUDIO_BYTES) {
      this.#fail(new Error("Recording exceeded the 7 MiB limit. Make a shorter recording."));
      return;
    }
    if (!this.#bytes) {
      this.#fail(new Error("Recorder stopped without producing audio data."));
      return;
    }
    const blob = new Blob(this.#chunks, { type: this.#diagnostics.mimeType });
    await this.callbacks.onAudio({
      blob,
      mimeType: blob.type || this.#diagnostics.mimeType,
      durationMs: Math.min(MAX_RECORDING_MS, Math.round(performance.now() - this.#startedAt)),
      diagnostics: { ...this.#diagnostics },
    });
  }

  #transition(next: RecordingState): void {
    this.machine.transition(next);
    this.callbacks.onState(next);
  }

  #fail(error: unknown): void {
    const source = error && typeof error === "object" ? error as { name?: unknown; message?: unknown } : undefined;
    const normalized = error instanceof Error
      ? error
      : Object.assign(new Error(typeof source?.message === "string" ? source.message : "Unknown recorder error."), {
          name: typeof source?.name === "string" ? source.name : "Error",
        });
    this.#diagnostics.errorName = normalized.name;
    this.#diagnostics.errorMessage = normalized.message;
    if (this.state !== "error" && this.machine.canTransition("error")) this.#transition("error");
    this.#emitDiagnostics();
    this.callbacks.onError(normalized);
  }

  #resetCapture(): void {
    this.#clearTimers();
    this.#stopTracks();
    this.#recorder = null;
    this.#chunks = [];
    this.#bytes = 0;
    this.#startedAt = 0;
    this.#discard = false;
    this.#tooLarge = false;
    this.#stopReason = "user";
    this.#diagnostics = {
      apiPresent: typeof navigator.mediaDevices?.getUserMedia === "function" && typeof globalThis.MediaRecorder === "function",
      mimeType: "not selected",
      dataAvailable: false,
      audioBytes: 0,
      stopEvent: false,
    };
  }

  #clearTimers(): void {
    if (this.#maxTimer !== undefined) window.clearTimeout(this.#maxTimer);
    if (this.#tickTimer !== undefined) window.clearInterval(this.#tickTimer);
    this.#maxTimer = undefined;
    this.#tickTimer = undefined;
  }

  #stopTracks(): void {
    this.#stream?.getTracks().forEach((track) => track.stop());
    this.#stream = null;
  }

  #emitDiagnostics(): void { this.callbacks.onDiagnostics({ ...this.#diagnostics }); }
}
